/**
 * Imprime UNA hoja concreta de la página.
 *
 * Marca el elemento destino, su cadena de ancestros y el body con los atributos
 * que consumen las reglas `@media print` de `app/globals.css`, imprime, y los
 * quita. El mecanismo tiene que ser compartido: cuando había dos imprimibles en
 * la misma guía (la plantilla estática y la carta del generador), cada uno con
 * su propio bloque `@media print` revelando la misma clase, al imprimir uno
 * salían los dos.
 *
 * La cadena de ancestros se marca porque ocultar con `visibility: hidden` no
 * basta: lo oculto SIGUE ocupando su sitio en el flujo de impresión. Con una
 * sola hoja eso solo añadía páginas en blanco detrás (nadie las notaba porque
 * se imprimía la 1); con el cuadernillo de Halloween, que son nueve hojas
 * dentro de un artículo largo, salían dieciséis páginas y las primeras en
 * negro. Sabiendo qué ancestros conservar, el CSS puede sacar del flujo
 * (`display: none`) todo lo que no es la hoja.
 *
 * **Las marcas se limpian al ENTRAR, no por temporizador.** Antes había un
 * `setTimeout(cleanUp, 1000)` de respaldo para el caso de que el navegador no
 * emitiera `afterprint`. El problema es que `window.print()` NO bloquea en
 * todos los navegadores: en muchos vuelve nada más abrir la vista previa, así
 * que ese respaldo desmarcaba el DOM con el diálogo todavía abierto, la previa
 * se regeneraba sin marcas y salía la página entera — el cuadernillo de nueve
 * hojas imprimía quince páginas con la landing, el banner de cookies y el aviso
 * de idioma. Limpiar lo rancio al entrar cubre el mismo caso (marcas olvidadas
 * de una impresión anterior) sin tocar la que está en curso, y las marcas no se
 * ven en pantalla mientras esperan. El `focus` cierra el resto: al volver a la
 * pestaña tras cerrar el diálogo, se limpia aunque `afterprint` no llegue.
 */

const PRINTING_ATTRIBUTE = 'data-printing'
const TARGET_ATTRIBUTE = 'data-print-active'
const ANCESTOR_ATTRIBUTE = 'data-print-ancestor'

/** Quita las marcas que haya dejado una impresión anterior que no se limpió. */
function clearStaleMarks() {
  document.body.removeAttribute(PRINTING_ATTRIBUTE)
  for (const marked of document.querySelectorAll(
    `[${TARGET_ATTRIBUTE}], [${ANCESTOR_ATTRIBUTE}]`
  )) {
    marked.removeAttribute(TARGET_ATTRIBUTE)
    marked.removeAttribute(ANCESTOR_ATTRIBUTE)
  }
}

export function printElement(element: HTMLElement): void {
  clearStaleMarks()

  const { body } = document
  element.setAttribute(TARGET_ATTRIBUTE, '')
  body.setAttribute(PRINTING_ATTRIBUTE, '')

  // Del padre hacia arriba, sin incluir el body: el body ya lleva su propio
  // atributo y el CSS lo trata aparte.
  const ancestors: HTMLElement[] = []
  for (let node = element.parentElement; node && node !== body; node = node.parentElement) {
    node.setAttribute(ANCESTOR_ATTRIBUTE, '')
    ancestors.push(node)
  }

  let cleanedUp = false
  function cleanUp() {
    if (cleanedUp) return
    cleanedUp = true
    element.removeAttribute(TARGET_ATTRIBUTE)
    body.removeAttribute(PRINTING_ATTRIBUTE)
    for (const ancestor of ancestors) ancestor.removeAttribute(ANCESTOR_ATTRIBUTE)
    window.removeEventListener('afterprint', cleanUp)
    window.removeEventListener('focus', cleanUp)
  }

  window.addEventListener('afterprint', cleanUp)
  // Red de seguridad para los navegadores que no emiten `afterprint`: al cerrar
  // el diálogo, la pestaña recupera el foco.
  window.addEventListener('focus', cleanUp, { once: true })
  window.print()
}
